import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Image from '@components/AppImage';

const LocationDetails = ({ location, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [imageError, setImageError] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'services', label: 'Services', icon: 'Stethoscope' },
  ];

  if (!location) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative">
          <div className="h-48 overflow-hidden">
            {!imageError ? (
              <Image
                src={location.image}
                alt={`${location.name} clinic`}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#08005f] to-[#eb2227] flex items-center justify-center">
                <Icon name="MapPin" size={48} className="text-white opacity-80" />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition"
          >
            <Icon name="X" size={16} />
          </button>
          <div className="absolute bottom-4 left-4 bg-white bg-opacity-95 rounded-lg p-4">
            <h2 className="font-heading font-bold text-xl text-text-primary mb-1">
              {location.name}
            </h2>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="MapPin" size={14} />
              <span className="text-sm">{location.address}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-3">
                  About This Location
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  {location.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="Clock" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Operating Hours</p>
                        <p className="text-sm text-text-secondary">{location.hours}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Phone" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Phone</p>
                        <p className="text-sm text-text-secondary">{location.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Mail" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Email</p>
                        <p className="text-sm text-text-secondary">{location.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="Car" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Parking</p>
                        <p className="text-sm text-text-secondary">
                          {location.features.some(f => f.toLowerCase().includes('parking')) 
                            ? 'Free parking available' 
                            : 'Limited parking available'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Bus" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Public Transport</p>
                        <p className="text-sm text-text-secondary">Matatu stop nearby</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Accessibility" size={16} className="text-primary" />
                      <div>
                        <p className="font-medium text-text-primary">Accessibility</p>
                        <p className="text-sm text-text-secondary">
                          {location.features.some(f => f.toLowerCase().includes('wheelchair')) 
                            ? 'Wheelchair accessible' 
                            : 'Limited accessibility'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Available Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {location.services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                    <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-text-primary">{service.name}</p>
                      <p className="text-sm text-text-secondary">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer - Removed Book Appointment Button */}
        <div className="p-6 border-t bg-surface">
          <div className="text-sm text-text-secondary">
            Need help choosing? Call us at {location.phone}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationDetails;